/**
 * Raffinerie engloutie (GDD section 10): rock pierced with galleries, sheets of oil under the surface, water at
 * the bottom (the galleries under the sea are flooded), and a few wooden derricks over the oil.
 * Dangers: fires in chain, oil rising to the surface. Key: drill into a sheet under the enemy, then set it alight.
 */
import { Physique, nextInt } from '@invaders/sim';
import { plainMap, type MapState } from '../state';
import { type Gen, ellipse, island, layer, rect, sea, settle, tunnel, waves } from './common';

const { ROCK, SAND, OIL, WOOD } = Physique;

/**
 * Radius of the galleries (20 to 22: a unit of 16 walks and jumps in them) and of the shafts (31 wide: it drops in);
 * half as large again as before (author's choice), the floors further apart.
 */
const GALLERY_R = 20;
const SHAFT_R = 15;

/** True if the ellipse, grown by `margin`, is all rock (so that an oil sheet does not leak into a gallery). */
function solidAround(g: Gen, cx: number, cy: number, rx: number, ry: number, margin: number): boolean {
  for (let y = cy - ry - margin; y <= cy + ry + margin; y++) {
    for (let x = cx - rx - margin; x <= cx + rx + margin; x++) {
      if (x < 0 || x >= g.w || y < 0 || y >= g.h || g.data[y * g.w + x] !== ROCK) return false;
    }
  }
  return true;
}

/** A wooden derrick: a lattice tower over a sheet of oil (it burns). */
function derrick(g: Gen, x: number, groundY: number): void {
  const h = 34;
  for (let k = 0; k <= h; k++) {
    const half = 7 - Math.trunc((k * 5) / h);
    const y = groundY - 1 - k;
    g.data[y * g.w + x - half] = WOOD;
    g.data[y * g.w + x + half] = WOOD;
    if (k % 8 === 0) rect(g, x - half, y, x + half, y, WOOD);
  }
  rect(g, x - 3, groundY - h - 3, x + 3, groundY - h - 1, WOOD);
}

export function generateRefinery(g: Gen): MapState {
  const { w, rng, seaY } = g;
  const coastL = Math.trunc((w * 10) / 100) + nextInt(rng, 13) - 6;
  const coastR = Math.trunc((w * 90) / 100) + nextInt(rng, 13) - 6;
  const relief = waves(g, 20, 3, 10, 8, 3, 25);
  const surface = island(g, coastL, coastR, 80, (x) => 128 + relief(x));
  const land0 = coastL + 70;
  const land1 = coastR - 70;

  // Three floors of galleries, wide enough to walk and jump in: one under the surface, one at sea level, one under
  // the sea (flooded by sea()).
  const floors = [seaY - 66 - nextInt(rng, 6), seaY + 2 - nextInt(rng, 6), seaY + 62 + nextInt(rng, 8)];
  for (let f = 0; f < floors.length; f++) {
    let x = land0 + nextInt(rng, 40);
    while (x < land1 - 60) {
      const len = 120 + nextInt(rng, 160);
      const x1 = Math.min(land1, x + len);
      const y0 = floors[f] + nextInt(rng, 13) - 6;
      const y1 = floors[f] + nextInt(rng, 13) - 6;
      tunnel(g, x, y0, x1, y1, GALLERY_R + nextInt(rng, 3), 2);
      // A shaft from this gallery up to the floor above (or to the surface from the top floor).
      const sx = x + 20 + nextInt(rng, Math.max(1, x1 - x - 40));
      const top = f === 0 ? surface[sx] - 2 : floors[f - 1];
      tunnel(g, sx, top, sx, y0, SHAFT_R, 1);
      x = x1 + 30 + nextInt(rng, 70);
    }
  }

  // Sheets of oil sealed in rock: under the surface, and between the two upper floors of galleries.
  let sheets = 0;
  for (let attempt = 0; attempt < 400 && sheets < 7; attempt++) {
    const rx = 36 + nextInt(rng, 34);
    const ry = 6 + nextInt(rng, 4);
    const cx = land0 + rx + nextInt(rng, Math.max(1, land1 - land0 - 2 * rx));
    const shallow = attempt % 2 === 0;
    const from = shallow ? surface[cx] + 12 + ry : floors[0] + 24 + ry;
    const to = shallow ? floors[0] - 30 - ry : floors[1] - 24 - ry;
    if (to < from) continue;
    const cy = from + nextInt(rng, to - from + 1);
    if (!solidAround(g, cx, cy, rx, ry, 4)) continue;
    ellipse(g, cx, cy, rx, ry, OIL, ROCK);
    if (shallow && Math.abs(surface[cx - 7] - surface[cx + 7]) < 6 && !g.data.subarray((surface[cx] - 40) * w + cx - 8, (surface[cx] - 40) * w + cx + 8).includes(WOOD)) {
      derrick(g, cx, surface[cx]);
    }
    sheets++;
  }

  // Sand on the beaches.
  layer(g, surface, coastL - 60, coastL + 40, (x) => Math.max(0, 6 - Math.trunc(Math.abs(x - coastL) / 10)), SAND);
  layer(g, surface, coastR - 40, coastR + 60, (x) => Math.max(0, 6 - Math.trunc(Math.abs(x - coastR) / 10)), SAND);

  sea(g);
  settle(g);
  return plainMap('refinery');
}
